import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Image } from "react-bootstrap";
import { Icon } from "@iconify/react";
import "./cms.scss";

/**
 * Landing page content from the admin panel (« Landing Page » screen).
 *
 * Every editable text or image is wrapped in <Txt k="…"> / <Img k="…"> inside
 * a <Section id="…" label="…">. The built-in default of each field is the
 * page's original content, so nothing changes until the admin publishes.
 *
 * Visitors get the published content from the API.
 *
 * Draft view: the admin panel loads this page in an iframe with ?cms=preview.
 * The page then tells the admin which sections and fields it has ("schema"),
 * shows whatever draft content the admin sends ("content"), and outlines a
 * section with an edit button on hover; clicking it asks the admin to open
 * that section's editor ("edit-section"). The page never calls the admin API
 * itself — the admin panel does all saving — so no login is needed here.
 */

const API = "https://omni-cave.com/backend/api/v1/landing";
// Waits this long for published content before showing the page anyway, so a
// slow or failed API call never leaves the page blank.
const CONTENT_WAIT_MS = 1500;

/** How much longer than the original a text may be and still fit the layout. */
export const defaultMaxLength = (fallback) =>
  Math.max(20, Math.ceil((String(fallback).length * 1.3) / 10) * 10);

const isPreviewRequest = () =>
  window.parent !== window &&
  new URLSearchParams(window.location.search).get("cms") === "preview";

/** Published SEO, for crawlers that run JavaScript (Google). */
const applySeo = (seo = {}) => {
  if (seo.title) document.title = seo.title;
  if (seo.description !== undefined) {
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("name", "description");
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", seo.description);
  }
};

/** "buy_step1_title" in section "buy" → "Step 1 title". */
const labelFromKey = (key, sectionId) => {
  const rest = key.startsWith(`${sectionId}_`) ? key.slice(sectionId.length + 1) : key;
  const words = rest
    .replace(/_img$/, "_image")
    .replace(/(\D)(\d+)/g, "$1 $2")
    .replace(/(^|_)cta(?=_|$)/, "$1button")
    .split("_")
    .join(" ")
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const CmsContext = createContext({
  preview: false,
  value: (key, fallback) => fallback,
  isUnpublished: () => false,
  register: () => {},
  registerSection: () => {},
  postToAdmin: () => {},
});
const SectionContext = createContext(null);

export function CmsProvider({ children }) {
  const [preview] = useState(isPreviewRequest);
  const [content, setContent] = useState({});
  const [live, setLive] = useState({});
  const [ready, setReady] = useState(false);

  // Sections and fields rendered on the page, for the admin's editor.
  const sections = useRef(new Map());
  const parentOrigin = useRef("");
  const schemaTimer = useRef(null);

  const postToAdmin = useCallback((message) => {
    if (window.parent === window) return;
    window.parent.postMessage(
      { source: "omnicave-landing", ...message },
      parentOrigin.current || "*"
    );
  }, []);

  const sendSchema = useCallback(() => {
    clearTimeout(schemaTimer.current);
    schemaTimer.current = setTimeout(() => {
      const list = [...sections.current.values()]
        .sort((a, b) => a.order - b.order)
        .map((s) => ({ id: s.id, label: s.label, fields: [...s.fields.values()] }));
      postToAdmin({ type: "schema", sections: list });
    }, 200);
  }, [postToAdmin]);

  // React runs a child's effects before its parent's, so a field usually
  // registers before its <Section>: whichever comes first creates the entry,
  // which also keeps sections in page order.
  const sectionEntry = useCallback((id) => {
    if (!sections.current.has(id)) {
      sections.current.set(id, {
        id,
        label: id,
        order: sections.current.size,
        fields: new Map(),
      });
    }
    return sections.current.get(id);
  }, []);

  const register = useCallback(
    (sectionId, field) => {
      sectionEntry(sectionId).fields.set(field.key, field);
      sendSchema();
    },
    [sectionEntry, sendSchema]
  );

  const registerSection = useCallback(
    (id, label) => {
      sectionEntry(id).label = label;
      sendSchema();
    },
    [sectionEntry, sendSchema]
  );

  // Visitors: published content.
  useEffect(() => {
    if (preview) return undefined;
    let done = false;
    const show = () => {
      if (!done) {
        done = true;
        setReady(true);
      }
    };
    const timer = setTimeout(show, CONTENT_WAIT_MS);
    fetch(`${API}/content`)
      .then((r) => r.json())
      .then((res) => {
        if (res?.success) {
          setContent(res.body.content || {});
          applySeo(res.body.seo);
        }
      })
      .catch(() => {})
      .finally(() => {
        clearTimeout(timer);
        show();
      });
    return () => clearTimeout(timer);
  }, [preview]);

  // Draft view: content comes from the admin panel.
  useEffect(() => {
    if (!preview) return undefined;
    const onMessage = (e) => {
      if (e.data?.source !== "omnicave-admin") return;
      if (parentOrigin.current && e.origin !== parentOrigin.current) return;
      if (e.data.type === "content") {
        parentOrigin.current = e.origin;
        setContent(e.data.draft || {});
        setLive(e.data.live || {});
        setReady(true);
        sendSchema();
      } else if (e.data.type === "scroll-to" && e.data.id) {
        document
          .querySelector(`[data-cms-section="${e.data.id}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    window.addEventListener("message", onMessage);
    postToAdmin({ type: "ready" });
    // Show the page with its defaults even if the admin is slow to answer.
    const timer = setTimeout(() => setReady(true), CONTENT_WAIT_MS);
    return () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
    };
  }, [preview, postToAdmin, sendSchema]);

  const value = useCallback(
    (key, fallback) =>
      content[key] !== undefined && content[key] !== "" ? content[key] : fallback,
    [content]
  );

  /** A field changed in the draft but not published yet. */
  const isUnpublished = useCallback(
    (key) => (content[key] ?? null) !== (live[key] ?? null),
    [content, live]
  );

  const ctx = useMemo(
    () => ({ preview, value, isUnpublished, register, registerSection, postToAdmin }),
    [preview, value, isUnpublished, register, registerSection, postToAdmin]
  );

  return (
    <CmsContext.Provider value={ctx}>
      <div
        // `.cms-ready` also starts the hero's entrance animations (Hero.scss),
        // so they are not over before the page becomes visible.
        className={
          [preview && "cms-preview", ready && "cms-ready"].filter(Boolean).join(" ") || undefined
        }
        style={{ visibility: ready ? "visible" : "hidden" }}
      >
        {children}
      </div>
    </CmsContext.Provider>
  );
}

export const useCms = () => useContext(CmsContext);

/**
 * A block of the page the admin edits as one unit. In the draft view it is
 * outlined on hover with an « Edit » button that opens its editor in the
 * admin panel. For visitors it renders its children unchanged.
 */
export function Section({ id, label, children }) {
  const { preview, registerSection, postToAdmin } = useCms();
  const box = useRef(null);

  useEffect(() => {
    if (preview) registerSection(id, label);
  }, [preview, id, label, registerSection]);

  // The header floats over the top of the banner, so the banner's edit button
  // must sit below it (see cms.scss). Publish the header's height as a CSS
  // variable and keep it current when the window is resized.
  useEffect(() => {
    if (!preview || id !== "header" || !box.current) return undefined;
    const root = document.documentElement;
    const update = () => root.style.setProperty("--cms-header-h", `${box.current.offsetHeight}px`);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(box.current);
    return () => observer.disconnect();
  }, [preview, id]);

  if (!preview) {
    return <SectionContext.Provider value={id}>{children}</SectionContext.Provider>;
  }

  const open = (e) => {
    e.preventDefault();
    e.stopPropagation();
    postToAdmin({ type: "edit-section", id });
  };

  return (
    <SectionContext.Provider value={id}>
      <div className="cms-section" data-cms-section={id} ref={box}>
        {children}
        <div className="cms-section__bar">
          <span className="cms-section__name">{label}</span>
          <button type="button" className="cms-section__edit" onClick={open}>
            <Icon icon="mdi:pencil" /> Modifier
          </button>
        </div>
      </div>
    </SectionContext.Provider>
  );
}

/** Registers a field with its section for the admin's editor (draft view only). */
const useField = (field) => {
  const { preview, register } = useCms();
  const sectionId = useContext(SectionContext);
  const json = JSON.stringify(field);
  useEffect(() => {
    if (!preview || !sectionId) return;
    const f = JSON.parse(json);
    register(sectionId, { ...f, label: f.label || labelFromKey(f.key, sectionId) });
  }, [preview, sectionId, json, register]);
};

const withLineBreaks = (text) =>
  String(text)
    .split("\n")
    .map((line, i, all) => (
      <React.Fragment key={i}>
        {line}
        {i < all.length - 1 && <br />}
      </React.Fragment>
    ));

/** Editable text. `children` is the built-in default; "\n" becomes a line break. */
export function Txt({ k, children, label, maxLength }) {
  const { preview, value, isUnpublished } = useCms();
  const fallback = typeof children === "string" ? children : String(children ?? "");
  useField({
    key: k,
    kind: "text",
    label,
    default: fallback,
    maxLength: maxLength || defaultMaxLength(fallback),
  });
  const text = withLineBreaks(value(k, fallback));
  if (preview && isUnpublished(k)) return <span className="cms-changed">{text}</span>;
  return text;
}

/**
 * Editable image. `src` is the built-in default; `slotWidth`/`slotHeight`
 * are the pixel size the slot was designed for — uploads must keep that shape.
 * Any other prop (width, className…) goes to the <Image>.
 */
export function Img({ k, src, label, slotWidth, slotHeight, ...imgProps }) {
  const { preview, value, isUnpublished } = useCms();
  useField({ key: k, kind: "image", label, default: src, slotWidth, slotHeight });
  const changed = preview && isUnpublished(k);
  return (
    <Image
      {...imgProps}
      className={`${imgProps.className || ""}${changed ? " cms-changed-img" : ""}`.trim()}
      src={value(k, src)}
    />
  );
}

/** URL of an editable background image (registered with <BgTools>). */
export const useCmsImage = (k, src) => useCms().value(k, src);

/** Registers a section's background image as an editable field. Renders nothing. */
export function BgTools({ k, src, label, slotWidth, slotHeight }) {
  useField({
    key: k,
    kind: "image",
    label: label || "Background image",
    default: src,
    slotWidth,
    slotHeight,
  });
  return null;
}
